import React, { forwardRef, useImperativeHandle } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import FormComponent from './index.jsx';
import styles from '../../styles/formComponent/FormWrapper.module.scss';

const FormWrapper = forwardRef(({
  schema,
  defaultValues = {},
  onSubmit,
  children,
  fields = [],
  className = '',
  formProps = {},
  ...props
}, ref) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty, isValid },
    watch,
    setValue,
    getValues,
    clearErrors,
    setError
  } = useForm({
    resolver: schema ? zodResolver(schema) : undefined,
    defaultValues,
    mode: 'onChange', // Validate on change for better UX
    ...formProps
  });

  const onFormSubmit = async (data) => {
    try {
      if (onSubmit) {
        await onSubmit(data, { reset, setValue, getValues, clearErrors, setError });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  // Expose form methods through ref
  useImperativeHandle(ref, () => ({
    getValues,
    setValue,
    reset,
    resetForm: (newValues) => reset(newValues || defaultValues),
    clearErrors,
    setError,
    watch,
    handleSubmit,
    submit: () => handleSubmit(onFormSubmit)(),
    formState: { errors, isSubmitting, isDirty, isValid }
  }), [getValues, setValue, reset, clearErrors, setError, watch, handleSubmit, onFormSubmit, errors, isSubmitting, isDirty, isValid, defaultValues]);

  const handleReset = () => {
    reset(defaultValues);
  };

  const renderField = (field, index) => {
    const {
      name,
      type = 'input',
      label,
      placeholder,
      required = false,
      disabled = false,
      options = [],
      validation = {},
      error,
      customActionOnChange,
      ...fieldProps
    } = field;

    return (
      <div key={`${name}-${index}`} className={styles.fieldContainer}>
        <Controller
          name={name}
          control={control}
          render={({ field: controllerField, fieldState }) => {
            const handleChange = (event) => {
              controllerField.onChange(event);
              if (customActionOnChange) {
                const value = event?.target?.value ?? event;
                customActionOnChange(name, value);
              }
            };

            return (
              <FormComponent
                type={type}
                label={label}
                name={name}
                value={controllerField.value || (type === 'select' && fieldProps.multiple ? [] : '')}
                onChange={handleChange}
                onBlur={controllerField.onBlur}
                placeholder={placeholder}
                required={required}
                disabled={disabled || isSubmitting}
                error={fieldState.error?.message || error}
                options={options}
                checked={type === 'checkbox' || type === 'switch' ? Boolean(controllerField.value) : undefined}
                {...fieldProps}
              />
            );
          }}
        />
      </div>
    );
  };

  return (
    <div className={`${styles.formWrapper} ${className}`}>
      <form onSubmit={handleSubmit(onFormSubmit)} className={styles.form} {...props}>
        {/* Render fields from fields prop */}
        {fields.length > 0 && (
          <div className={styles.fieldsContainer}>
            {fields.map((field, index) => renderField(field, index))}
          </div>
        )}

        {/* Render custom children with form context */}
        {children && (
          <div className={styles.customFieldsContainer}>
            {typeof children === 'function' 
              ? children({ 
                  control, 
                  errors, 
                  watch, 
                  setValue, 
                  getValues, 
                  isSubmitting, 
                  isDirty, 
                  isValid,
                  FormComponent,
                  Controller
                })
              : children
            }
          </div>
        )}
      </form>
    </div>
  );
});

FormWrapper.displayName = 'FormWrapper';

export default FormWrapper;
